import { QuantityNames } from 'merchandise-lib/constants/QuantityName';
import Products from 'merchandise-lib/constants/Product';
import { parseTierPrice } from '../price/tierPricing';
import { getFscPreferredPurchaseOption, resolveFscMaxQuantity } from './fscCatalogHelpers';
/* Reserved pack ids. `included` is the free ZERO baseline; `other` is the
 * custom committed-credit amount. Any other id is a preset pack's creditPackSize. */
export const FSC_INCLUDED_PACK_ID = 'included';
export const FSC_OTHER_PACK_ID = 'other';

/* SMALL/MEDIUM/LARGE are the three fixed committed-credit pack sizes (formerly
 * the starter/professional/enterprise agents packs). */

export function isPresetCreditPackSize(size) {
  return size === 'SMALL' || size === 'MEDIUM' || size === 'LARGE';
}
const FSC_CREDIT_PACK_SIZES = ['ZERO', 'SMALL', 'MEDIUM', 'LARGE', 'CUSTOM'];
const isFscCreditPackSize = value => FSC_CREDIT_PACK_SIZES.some(size => size === value);
const getFscCreditPackSize = option => {
  var _option$creditPackOpt;
  const size = (_option$creditPackOpt = option.creditPackOption) === null || _option$creditPackOpt === void 0 ? void 0 : _option$creditPackOpt.creditPackSize;
  return isFscCreditPackSize(size) ? size : undefined;
};

/**
 * Which pack the shopper has chosen. `included` is the free ZERO baseline (no
 * pack); `preset` is a fixed pack identified by its creditPackSize (all preset packs
 * share the committed SKU), carrying the resolved committed-SKU quantity so the
 * cart serializes one-way without the plan options; `custom` is a chosen
 * quantity of the committed-credit SKU ("Other").
 */

/* The default "no pack" selection — the free ZERO baseline. */
export const FSC_INCLUDED_CREDIT_PACK_SELECTION = {
  kind: 'included'
};

/** Value equality for two selections — lets the picker tell a genuine
 * navigation (a different selection) from our own lossless URL-write echo. */
export const isSameFscCreditPackSelection = (a, b) => {
  if (a.kind === 'preset' && b.kind === 'preset') {
    return a.creditPackSize === b.creditPackSize && a.committedSkuQuantity === b.committedSkuQuantity;
  }
  if (a.kind === 'custom' && b.kind === 'custom') {
    return a.committedSkuQuantity === b.committedSkuQuantity;
  }
  return a.kind === 'included' && b.kind === 'included';
};
const PACK_SIZE_ORDER = {
  SMALL: 0,
  MEDIUM: 1,
  LARGE: 2
};
/*
 * Monthly cost of this option's pack above the ZERO baseline, as computed by
 * the backend. Term precedence matches getFscPreferredPurchaseOption,
 * since a portal may be offered only one term (manage portals return ANNUAL
 * with no MONTHLY key at all).
 */
function getIncrementalMonthlyPrice(option, preferredTerm) {
  var _parseTierPrice, _ref;
  const byTerm = option.incrementalPriceByTerm;
  const preferred = preferredTerm ? byTerm === null || byTerm === void 0 ? void 0 : byTerm[preferredTerm] : undefined;
  return (_parseTierPrice = parseTierPrice((_ref = preferred !== null && preferred !== void 0 ? preferred : byTerm === null || byTerm === void 0 ? void 0 : byTerm.ANNUAL) !== null && _ref !== void 0 ? _ref : byTerm === null || byTerm === void 0 ? void 0 : byTerm.MONTHLY)) !== null && _parseTierPrice !== void 0 ? _parseTierPrice : 0;
}

/** Credits bundled with the plan's primary product — the "Included" baseline. */
export const getFscIncludedCredits = (tierData, tier) => {
  var _tierData$productsByT, _tierData$products$pr, _tierData$products;
  const primaryApiName = (_tierData$productsByT = tierData.productsByTier) === null || _tierData$productsByT === void 0 || (_tierData$productsByT = _tierData$productsByT[tier]) === null || _tierData$productsByT === void 0 ? void 0 : _tierData$productsByT.productApiName;
  if (!primaryApiName) {
    return 0;
  }
  return (_tierData$products$pr = (_tierData$products = tierData.products) === null || _tierData$products === void 0 || (_tierData$products = _tierData$products[primaryApiName]) === null || _tierData$products === void 0 || (_tierData$products = _tierData$products.packagedQuantitiesMap) === null || _tierData$products === void 0 ? void 0 : _tierData$products[QuantityNames.HUBSPOT_CREDITS]) !== null && _tierData$products$pr !== void 0 ? _tierData$products$pr : 0;
};

/** The free "no pack" baseline option (ZERO credit pack), if present. */
export const getFscZeroCreditOption = options => options.find(option => {
  var _option$creditPackOpt2;
  return getFscCreditPackSize(option) === 'ZERO' || ((_option$creditPackOpt2 = option.creditPackOption) === null || _option$creditPackOpt2 === void 0 ? void 0 : _option$creditPackOpt2.creditPack) === 0;
});

/**
 * Shapes the fixed (preset) agents packs from the product-plan options into the
 * card data the picker needs, sorted starter → professional → enterprise. Each
 * pack's price is its monthly cost above the ZERO baseline, so it reads as the
 * incremental cost of adding the pack.
 */
export const buildFscPresetCreditPacks = (options, preferredTerm) => {
  const packs = [];
  for (const option of options) {
    const creditPackOption = option.creditPackOption;
    if (!creditPackOption) {
      continue;
    }
    const size = getFscCreditPackSize(option);
    if (!size || !isPresetCreditPackSize(size)) {
      continue;
    }
    packs.push({
      id: size,
      creditPackSize: size,
      credits: creditPackOption.creditPack,
      pricePerMonth: getIncrementalMonthlyPrice(option, preferredTerm),
      isRecommended: creditPackOption.isRecommended
    });
  }
  packs.sort((a, b) => PACK_SIZE_ORDER[a.creditPackSize] - PACK_SIZE_ORDER[b.creditPackSize]);
  return packs;
};
/** The backend's projected-credits breakdown off the first option carrying it, or undefined when absent. */
export const getFscProjectedCreditsSummary = options => {
  for (const option of options) {
    var _option$creditPackOpt3;
    const projected = (_option$creditPackOpt3 = option.creditPackOption) === null || _option$creditPackOpt3 === void 0 ? void 0 : _option$creditPackOpt3.projectedCredits;
    if (projected) {
      return {
        includedCreditsAfterPurchase: projected.includedCreditsAfterPurchase,
        additionalCreditsOwned: projected.additionalCreditsOwned
      };
    }
  }
  return undefined;
};

/** The AgentPacksSection selectedPackId string for a given selection. */
export const getFscSelectedPackId = selection => {
  if (selection.kind === 'preset') {
    return selection.creditPackSize;
  }
  if (selection.kind === 'custom') {
    return FSC_OTHER_PACK_ID;
  }
  return FSC_INCLUDED_PACK_ID;
};

/* Selecting "Other" starts at zero units, so the shopper is charged for nothing
 * they haven't asked for. The unit price needed to drive the stepper comes from
 * the cart-independent price catalog, not from this selection. */
const INITIAL_CUSTOM_UNITS = 0;
export const buildFscCreditPackSelection = (packId, presetCommittedSkuQuantity) => {
  if (packId === FSC_INCLUDED_PACK_ID) {
    return {
      kind: 'included'
    };
  }
  if (packId === FSC_OTHER_PACK_ID) {
    return {
      kind: 'custom',
      committedSkuQuantity: INITIAL_CUSTOM_UNITS
    };
  }
  if (!isPresetCreditPackSize(packId)) {
    return null;
  }
  return {
    kind: 'preset',
    creditPackSize: packId,
    committedSkuQuantity: presetCommittedSkuQuantity
  };
};

/**
 * The committed-SKU quantity a preset pack size sells, read off its plan option.
 * Resolved at selection time so a preset selection carries its own quantity and
 * serializes one-way to the URL without needing the plan options.
 */
export const getFscPresetPackCommittedSkuQuantity = (options, packId) => {
  var _match$creditPackOpti, _match$creditPackOpti2;
  const match = options.find(option => {
    var _option$creditPackOpt4;
    return ((_option$creditPackOpt4 = option.creditPackOption) === null || _option$creditPackOpt4 === void 0 ? void 0 : _option$creditPackOpt4.creditPackSize) === packId;
  });
  return (_match$creditPackOpti = match === null || match === void 0 || (_match$creditPackOpti2 = match.creditPackOption) === null || _match$creditPackOpti2 === void 0 ? void 0 : _match$creditPackOpti2.productAndSkuQuantity.skuQuantity) !== null && _match$creditPackOpti !== void 0 ? _match$creditPackOpti : 0;
};

/** The custom ("Other") committed-credit option, if the plan returned one. */
const getFscCustomCreditOption = options => options.find(option => getFscCreditPackSize(option) === 'CUSTOM');

/**
 * Picks the product-plan option that matches the current selection so the order
 * summary prices seats + the chosen pack. Falls back to the ZERO baseline when
 * a preset pack can't be matched (e.g. it dropped out of the offered set) or a
 * custom amount hasn't been priced yet.
 */
export const selectFscCreditPackPlanOption = (options, selection) => {
  if (selection.kind === 'preset') {
    const match = options.find(option => {
      var _option$creditPackOpt5;
      return ((_option$creditPackOpt5 = option.creditPackOption) === null || _option$creditPackOpt5 === void 0 ? void 0 : _option$creditPackOpt5.creditPackSize) === selection.creditPackSize;
    });
    if (match) {
      return match;
    }
  }
  if (selection.kind === 'custom') {
    const custom = getFscCustomCreditOption(options);
    if (custom) {
      return custom;
    }
  }
  return getFscZeroCreditOption(options);
};

/**
 * The credit product to add to the order line items and purchase request for a
 * selected option. Returns null for the ZERO baseline (no pack to add).
 */
export const getFscSelectedCreditProduct = option => {
  var _option$creditPackOpt6;
  const productAndSkuQuantity = option === null || option === void 0 || (_option$creditPackOpt6 = option.creditPackOption) === null || _option$creditPackOpt6 === void 0 ? void 0 : _option$creditPackOpt6.productAndSkuQuantity;
  if (!productAndSkuQuantity || productAndSkuQuantity.skuQuantity <= 0) {
    return null;
  }
  return {
    productApiName: productAndSkuQuantity.productApiName,
    incrementalQuantity: productAndSkuQuantity.skuQuantity
  };
};

/* ── Custom ("Other") committed-credit amount ──────────────────────────────
 * The committed-credit SKU is an eligible tier product, so its credits-per-unit
 * and max quantity come from the tier-options `products` map. Its price is not
 * in tier options (tier pricing only prices the core seat), so the per-unit
 * price is read from the CUSTOM product-plan option once a unit is in the cart.
 */

/** Credits bundled with one committed-credit SKU unit. */
export const getFscCommittedCreditsPerUnit = tierData => {
  var _tierData$products$Pr, _tierData$products2;
  return (_tierData$products$Pr = (_tierData$products2 = tierData.products) === null || _tierData$products2 === void 0 || (_tierData$products2 = _tierData$products2[Products.HUBSPOT_CREDITS_COMMITTED]) === null || _tierData$products2 === void 0 || (_tierData$products2 = _tierData$products2.packagedQuantitiesMap) === null || _tierData$products2 === void 0 ? void 0 : _tierData$products2[QuantityNames.HUBSPOT_CREDITS]) !== null && _tierData$products$Pr !== void 0 ? _tierData$products$Pr : 0;
};

/** Largest committed-credit quantity the stepper can reach (touchless cap). */
export const getFscCommittedMaxUnits = tierData => {
  var _tierData$products3;
  return resolveFscMaxQuantity((_tierData$products3 = tierData.products) === null || _tierData$products3 === void 0 ? void 0 : _tierData$products3[Products.HUBSPOT_CREDITS_COMMITTED]);
};

/**
 * Per-unit committed-credit price, read off the CUSTOM plan option's own
 * committed-credit line. Returns undefined until a CUSTOM option has been
 * priced — the backend omits the option entirely at zero units, which is why
 * the price catalog sources this from its own cart rather than the shopper's.
 */
export const getFscCommittedUnitPrice = (options, preferredTerm) => {
  var _getFscPreferredPurch;
  const customOption = getFscCustomCreditOption(options);
  if (!customOption) {
    return undefined;
  }
  const unitPrice = parseTierPrice((_getFscPreferredPurch = getFscPreferredPurchaseOption(customOption, preferredTerm)) === null || _getFscPreferredPurch === void 0 || (_getFscPreferredPurch = _getFscPreferredPurch.productPrices) === null || _getFscPreferredPurch === void 0 || (_getFscPreferredPurch = _getFscPreferredPurch[Products.HUBSPOT_CREDITS_COMMITTED]) === null || _getFscPreferredPurch === void 0 ? void 0 : _getFscPreferredPurch.unitPrice);
  return unitPrice !== undefined && unitPrice > 0 ? unitPrice : undefined;
};

/** Builds the AgentPacksSection custom-stepper config from the resolved inputs. */
export const buildFscCustomCreditConfig = ({
  unitPrice,
  creditsPerUnit,
  maxUnits,
  committedSkuQuantity
}) => ({
  monthlyCost: unitPrice * committedSkuQuantity,
  credits: creditsPerUnit * committedSkuQuantity,
  stepSize: unitPrice,
  maxMonthlyCost: unitPrice * maxUnits,
  disabled: unitPrice <= 0
});

/** Maps a stepped money value to a committed-credit quantity (round, clamped). */
export const fscMonthlyCostToUnits = (monthlyCost, unitPrice, maxUnits) => {
  if (unitPrice <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(maxUnits, Math.round(monthlyCost / unitPrice)));
};

/**
 * Snaps a typed free-entry money value to a whole committed-credit quantity:
 * floor, min 1 unit for any positive amount, 0 for a non-positive amount.
 */
export const fscSnapMonthlyCostToUnits = (monthlyCost, unitPrice, maxUnits) => {
  if (unitPrice <= 0 || monthlyCost <= 0) {
    return 0;
  }
  return Math.max(1, Math.min(maxUnits, Math.floor(monthlyCost / unitPrice)));
};